import { PayloadAction } from "@reduxjs/toolkit";
import { takeLatest, all, call, put, select } from "redux-saga/effects";

import { HttpError, HttpStatusCodes } from "infrastructure/http";
import { ILocalizable, ILocale, commonMessages, isLocalizable } from "infrastructure/localization";
import { notification, INotification } from "infrastructure/notification";
import { IValidationIssue, ValidationIssueSeverity } from "infrastructure/validation";
import { IPickerSearchCriterion, applyPickerFilter } from "ui/shared/helpers/plex-components";
import { extractApiErrorMessageContent, ITenant, IEnterprise, IIndustry } from "ui/shared/models/common";

import { mapParentRequest } from "../shared/helpers/ParentFlag.helper";
import { createTextPreview } from "../shared/helpers/TextPreview.helpers";
import { IChangeToken } from "../shared/types/Token.types";
import { displayMessages } from "../translation-managers.localization";

import { translationMessagesActions } from "./TranslationMessages.actions";
import { translationMessagesManagerApi as api } from "./TranslationMessages.api";
import { ITranslationsObsoleteRequest, ITranslationsObsolete, ITranslationMessagesGetRequest, ITranslationMessageRequest } from "./TranslationMessages.api.types";
import { getTranslationModifyData } from "./TranslationMessages.helpers";
import {
  selectors,
  ITranslationMessagesManagerFilter,
  ITranslationMessagesManagerFilterData,
  ITranslationMessageItem,
  ITranslationMessageRow,
  IChangeText,
  ITranslationMessageModifyData,
  ITranslationErrorModel,
  ITenantsPickerSearchCriterion,
  SelectorState
} from "./TranslationMessages.state";

function* getChangedRows() {
  const rows: ITranslationMessageRow[] = yield select(selectors.getGridRows);
  return rows.filter(row => row.isTranslationChanged);
}

function* getNotValidRows() {
  const rows: ITranslationMessageRow[] = yield select(selectors.getGridRows);
  return rows.filter(row => row.validationIssues.length);
}

function* resetValidationState() {
  const rows: ITranslationMessageRow[] = yield call(getNotValidRows);

  if (rows.length) {
    const updatedRecords = rows.map(r => {
      return getTranslationModifyData(r, [], r.data.translatedText);
    });

    yield put(translationMessagesActions.applyTranslationsChanges(updatedRecords));
  }
}

function* initializeExecute() {
  try {
    const [translatedBy, parents]: [ILocalizable[], ILocalizable[]] = yield all([call(api.getTranslatedBy), call(api.getParentOptions)]);

    const filter: ITranslationMessagesManagerFilter = {
      locales: [],
      industries: [],
      enterprises: [],
      tenants: [],
      text: "",
      messageId: "",
      translatedBy: translatedBy,
      parents: parents,
      isCheckedShowPreviewText: false,
      supportedLocales: []
    };

    yield put(translationMessagesActions.initializeCompleted(filter));
  } catch (error) {
    yield put(translationMessagesActions.loadingFailed(notification.error(error).useMessageId(commonMessages.error("searchFailed"))));
  }
}

function* searchExecute() {
  try {
    const filter: ITranslationMessagesManagerFilter = yield select(selectors.getFilter);
    const searchData = filter.lastData;
    if (searchData) {
      if (!searchData.selectedLocale) {
        yield put(translationMessagesActions.loadingFailed(notification.warning().useMessageId(displayMessages.error("localeRequired"))));
      } else if (!searchData.selectedEnterprise && !searchData.selectedTenant) {
        yield put(translationMessagesActions.loadingFailed(notification.warning().useMessageId(displayMessages.error("enterpriseOrTenantRequired"))));
      } else {
        const request: ITranslationMessagesGetRequest = {
          locale: searchData.selectedLocale.localeIdentifier,
          industry: searchData.selectedIndustry && searchData.selectedIndustry.isiCv4Code,
          tenant: searchData.selectedTenant && searchData.selectedTenant.tenantId,
          enterprise: searchData.selectedEnterprise && searchData.selectedEnterprise.enterpriseId,
          text: searchData.filterText,
          messageId: searchData.filterMessageId,
          translatedBy: searchData.selectedTranslatedBy?.messageId.value,
          parent: searchData.selectedParent && mapParentRequest(searchData.selectedParent)
        };

        const plexTranslationMessages: ITranslationMessageItem[] = yield call(api.getTranslationMessages, request);

        const translationRows: ITranslationMessageRow[] = plexTranslationMessages.map((item, index) => {
          const tokens = item.tokens.map((t, index) => {
            return {
              index: index,
              name: t.name,
              description: t.description,
              type: t.type,
              value: null,
              originalValue: null,
              isTokenValueChanged: false
            };
          });

          return {
            data: {
              locale: item.locale,
              messageId: item.messageId,
              sourceText: item.sourceText,
              translatedText: item.translatedText,
              tokens: tokens,
              textPreview: createTextPreview(item.translatedText ? item.translatedText : "", tokens, item.locale),
              translationStatus: item.translationStatus,
              version: item.version,
              isParentMessage: item.isParentMessage
            },
            index: index,
            isTranslationChanged: false,
            isTranslationDefinitionChanged: false,
            validationIssues: []
          };
        });

        yield put(translationMessagesActions.searchCompleted(translationRows));
      }
    }
  } catch (error) {
    yield put(translationMessagesActions.loadingFailed(notification.error(error).useMessageId(commonMessages.error("searchFailed"))));
  }
}

function* changeTranslationExecute({ payload }: PayloadAction<IChangeText>) {
  const rows: ITranslationMessageRow[] = yield select(selectors.getGridRows);
  const row = rows[payload.index];

  const issues = [];
  const changedData = getTranslationModifyData(row, issues, payload.text);

  yield put(translationMessagesActions.applyTranslationChange(changedData));
}

function* changeTokenExecute({ payload }: PayloadAction<IChangeToken>) {
  yield put(translationMessagesActions.applyChangeTokenValue(payload));
}

function* getErrorRecords(error: HttpError) {
  const errorModels: ITranslationErrorModel[] = error.data;
  const rows: ITranslationMessageRow[] = yield select(selectors.getGridRows);

  const updatedRecords: ITranslationMessageModifyData[] = [];

  for (const row of rows) {
    const issues: IValidationIssue[] = [];

    for (const model of errorModels) {
      if (row.data.messageId === model.messageId) {
        issues.push({
          message: extractApiErrorMessageContent(model.error),
          severity: ValidationIssueSeverity.Error,
          tag: "Translation"
        });
      }
    }

    const data = getTranslationModifyData(row, issues, row.data.translatedText);
    updatedRecords.push(data);
  }

  return updatedRecords;
}

function* saveTranslationsExecute() {
  try {
    const filterData: ITranslationMessagesManagerFilterData | undefined = yield select(selectors.getFilterLastData);

    if (filterData) {
      const rows: ITranslationMessageRow[] = yield call(getChangedRows);

      const changedRows = rows.map(row => {
        const data = row.data;

        const rowData: ITranslationMessageRequest = {
          messageId: data.messageId,
          text: data.translatedText,
          locale: filterData.selectedLocale?.localeIdentifier,
          tenantId: filterData.selectedTenant?.tenantId,
          enterpriseId: filterData.selectedTenant?.tenantId ? undefined : filterData.selectedEnterprise?.enterpriseId,
          version: data.version
        };
        return rowData;
      });

      if (changedRows.length === 0) {
        yield call(resetValidationState);
        yield put(
          translationMessagesActions.saveTranslationsCompleted({ message: notification.warning().useMessageId(commonMessages.info("noRecordsModified")), isLoading: false })
        );
      } else {
        yield call(api.saveTranslations, changedRows);

        yield put(translationMessagesActions.search());
        yield put(translationMessagesActions.saveTranslationsCompleted({ message: notification.success().useMessageId(displayMessages.info("translationsSaveCompleted")) }));
      }
    }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const updatedRecords: ITranslationMessageModifyData[] = yield call(getErrorRecords, error);
      yield put(translationMessagesActions.applyTranslationsChanges(updatedRecords));
    }

    yield put(translationMessagesActions.loadingFailed(notification.error().useMessageId(displayMessages.error("translationsSaveFailed"))));
  }
}

function* applyTranslationChangesExecute() {
  const rows: ITranslationMessageRow[] = yield call(getChangedRows);
  const notifications: INotification[] = yield select(selectors.getNotifications);

  const warningMessageId = displayMessages.info("updatedItemsNotSaved");
  const warningShown = notifications.some(n => n.content.some(c => isLocalizable(c.message) && c.message.messageId.value === warningMessageId.value));

  if (rows.length > 0) {
    if (!warningShown) {
      yield put(translationMessagesActions.applyTranslationChangesWarning(notification.warning().useMessageId(warningMessageId)));
    }
  } else {
    if (warningShown) {
      yield put(translationMessagesActions.applyTranslationChangesWarning());
    }
  }
}

function* loadIndustriesExecute({ payload }: PayloadAction<IPickerSearchCriterion<IIndustry>>) {
  try {
    let industries: IIndustry[] = yield call(api.getIndustries, payload.query.query);
    industries = applyPickerFilter(industries, payload, row => row.description);

    yield put(translationMessagesActions.loadIndustriesCompleted(industries));
  } catch (error) {
    yield put(translationMessagesActions.loadingFailed(notification.error(error).useMessageId(displayMessages.error("loadIndustriesFailed"))));
  }
}

function* loadEnterprisesExecute({ payload }: PayloadAction<IPickerSearchCriterion<IEnterprise>>) {
  try {
    let enterprises: IEnterprise[] = yield call(api.getEnterprises, payload.query.query);
    enterprises = applyPickerFilter(enterprises, payload, row => row.name);

    yield put(translationMessagesActions.loadEnterprisesCompleted(enterprises));
  } catch (error) {
    yield put(translationMessagesActions.loadingFailed(notification.error(error).useMessageId(displayMessages.error("loadEnterprisesFailed"))));
  }
}

function* loadTenantsExecute({ payload }: PayloadAction<ITenantsPickerSearchCriterion>) {
  try {
    if (!payload.enterpriseId) {
      yield put(translationMessagesActions.loadingFailed(notification.warning().useMessageId(displayMessages.error("enterpriseRequired"))));
    } else {
      let tenants: ITenant[] = yield call(api.getTenants, payload.enterpriseId, payload.query.query);
      tenants = applyPickerFilter(tenants, payload, row => row.name);

      yield put(translationMessagesActions.loadTenantsCompleted(tenants));
    }
  } catch (error) {
    yield put(translationMessagesActions.loadingFailed(notification.error(error).useMessageId(displayMessages.error("loadTenantsFailed"))));
  }
}

function* loadLocalesExecute({ payload }: PayloadAction<IPickerSearchCriterion<ILocale>>) {
  try {
    let locales: ILocale[] = yield call(api.getLocales, payload?.query?.query);
    locales = applyPickerFilter(locales, payload, row => row.name);
    applyPickerFilter(locales, payload, row => row.localeIdentifier);

    yield put(translationMessagesActions.loadLocalesCompleted(locales));
  } catch (error) {
    yield put(translationMessagesActions.loadingFailed(notification.error(error).useMessageId(commonMessages.error("loadLocalesFailed"))));
  }
}

function* downloadXmlTemplateExecute() {
  try {
    yield call(api.downloadXmlTemplate);
  } catch (error) {
    yield put(translationMessagesActions.loadingFailed(notification.error(error).useMessageId(commonMessages.error("searchFailed"))));
  }
}

function* exportTranslationsToXmlExecute() {
  try {
    const filter: ITranslationMessagesManagerFilter = yield select(selectors.getFilter);
    const searchData = filter.lastData;
    if (searchData) {
      if (!searchData.selectedLocale) {
        yield put(translationMessagesActions.loadingFailed(notification.warning().useMessageId(displayMessages.error("localeRequired"))));
      } else {
        const request: ITranslationMessagesGetRequest = {
          locale: searchData.selectedLocale.localeIdentifier,
          industry: searchData.selectedIndustry?.isiCv4Code,
          tenant: searchData.selectedTenant?.tenantId,
          enterprise: searchData.selectedEnterprise?.enterpriseId,
          text: searchData.filterText,
          messageId: searchData.filterMessageId,
          translatedBy: searchData.selectedTranslatedBy?.messageId.value,
          parent: searchData.selectedParent && mapParentRequest(searchData.selectedParent)
        };

        yield call(api.exportTranslationsToXml, request);
      }
    }
  } catch (error) {
    yield put(translationMessagesActions.loadingFailed(notification.error(error).useMessageId(displayMessages.error("exportFailed"))));
  }
}

function* obsoleteTranslationsExecute() {
  try {
    const [rows, filterData]: [ITranslationMessageRow[], ITranslationMessagesManagerFilterData | undefined] = yield select((state: SelectorState) => [
      state.translationMessagesManager.grid.selected,
      state.translationMessagesManager.filter.lastData
    ]);

    if (filterData && filterData.selectedLocale && filterData.selectedEnterprise) {
      const selectedRows: ITranslationsObsolete[] = rows.map(row => {
        return {
          messageId: row.data.messageId,
          version: row.data.version
        };
      });

      const request: ITranslationsObsoleteRequest = {
        locale: filterData.selectedLocale.localeIdentifier,
        enterprise: filterData.selectedEnterprise.enterpriseId,
        tenant: filterData.selectedTenant?.tenantId,
        translations: selectedRows
      };

      if (request.tenant) {
        yield call(api.obsoleteTenantTranslations, request);
      } else if (request.enterprise) {
        yield call(api.obsoleteEnterpriseTranslations, request);
      }

      yield put(translationMessagesActions.search());
      yield put(translationMessagesActions.obsoleteTranslationsCompleted(notification.success().useMessageId(displayMessages.info("translationsObsoleteCompleted"))));
    }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const updatedRecords: ITranslationMessageModifyData[] = yield call(getErrorRecords, error);

      yield put(translationMessagesActions.applyTranslationsChanges(updatedRecords));
    }

    yield put(translationMessagesActions.loadingFailed(notification.error().useMessageId(displayMessages.error("translationsObsoleteFailed"))));
  }
}

function* actionWatcher() {
  yield takeLatest(translationMessagesActions.initialize.toString(), initializeExecute);
  yield takeLatest(translationMessagesActions.search.toString(), searchExecute);
  yield takeLatest(translationMessagesActions.loadIndustries.toString(), loadIndustriesExecute);
  yield takeLatest(translationMessagesActions.loadEnterprises.toString(), loadEnterprisesExecute);
  yield takeLatest(translationMessagesActions.loadTenants.toString(), loadTenantsExecute);
  yield takeLatest(translationMessagesActions.loadLocales.toString(), loadLocalesExecute);
  yield takeLatest(translationMessagesActions.saveTranslations.toString(), saveTranslationsExecute);
  yield takeLatest(translationMessagesActions.applyTranslationsChanges.toString(), applyTranslationChangesExecute);
  yield takeLatest(translationMessagesActions.changeTokenValue.toString(), changeTokenExecute);
  yield takeLatest(translationMessagesActions.changeTranslation.toString(), changeTranslationExecute);
  yield takeLatest(translationMessagesActions.downloadTranslationUploadTemplate.toString(), downloadXmlTemplateExecute);
  yield takeLatest(translationMessagesActions.exportTranslationsToXml.toString(), exportTranslationsToXmlExecute);
  yield takeLatest(translationMessagesActions.obsoleteTranslations.toString(), obsoleteTranslationsExecute);
}

export function* translationMessagesManagerSaga() {
  yield all([actionWatcher()]);
}
